import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LcACLType, LcACLCanType, LcACLConfig, AuthPathItem, AuthModelList } from './lc-acl.type';

/**
 *
 * 务必在根目录注册 `LcAclModule.forRoot()` 才能使用服务
 */
@Injectable()
export class LcAClService {
  private options: LcACLConfig;
  private roles: string[] = [];
  private types: string[] = [];
  private permissionGroups: string[] = [];
  private authPaths: Map<string, string> = new Map();
  private full = false;
  private aclChange = new BehaviorSubject<LcACLType | boolean | null>(null);

  /** ACL变更通知 */
  get change(): Observable<LcACLType | boolean | null> {
    return this.aclChange.asObservable();
  }

  /** 获取所有数据 */
  get data(): {
    full: boolean;
    roles: string[];
    permissionGroups: string[];
    authPaths: Map<string, string>;
  } {
    return {
      full: this.full,
      roles: this.roles,
      permissionGroups: this.permissionGroups,
      authPaths: this.authPaths,
    };
  }

  constructor() {}

  private parseLcACLType(val: LcACLCanType | null): LcACLType {
    let t: LcACLType;
    if (typeof val === 'string') {
      if (val.indexOf('::') > -1) {
        // permissionGroup
        t = { permissions: [val] };
      } else {
        // role
        t = { role: [val] };
      }
    } else if (typeof val === 'object' && !Array.isArray(val)) {
      t = { ...val };
    } else if (Array.isArray(val)) {
      if (val[0].toString().indexOf('::') > -1) {
        t = { permissions: val };
      } else {
        t = { role: val };
      }
    } else {
      t = { role: val == null ? [] : [val] };
    }

    return { except: false, ...t };
  }

  /**
   * 设置当前用户角色或权限能力（会先清除所有）
   */
  set(value: LcACLType): void {
    this.full = false;
    this.roles = [];
    this.types = [];
    this.permissionGroups = [];
    this.authPaths = new Map();
    this.add(value);
    this.aclChange.next(value);
  }

  /**
   * 标识当前用户为全量，即不受限
   */
  setFull(val: boolean): void {
    this.full = val;
    this.aclChange.next(val);
  }

  /**
   * 设置当前用户角色（会先清除所有）
   */
  setRole(roles: string[]): void {
    this.set({ role: roles } as LcACLType);
  }

  /**
   * 为当前用户增加角色或权限能力
   */
  add(value: LcACLType): void {
    if (value.role && value.role.length > 0) {
      this.roles.push(...value.role);
    }
    if (value.type && value.type.length > 0) {
      this.types.push(...value.type);
    }
    if (value.permissionGroups && value.permissionGroups.length > 0) {
      this.permissionGroups.push(...value.permissionGroups);
    }
    if (value.menus && value.menus.length > 0) {
      this.authPaths = this.getAuthPaths(value.menus);
    }
  }

  /**
   * 为当前用户附加角色
   */
  attachRole(roles: string[]): void {
    for (const val of roles) {
      if (!this.roles.includes(val)) {
        this.roles.push(val);
      }
    }
    this.aclChange.next(this.data);
  }

  /**
   * 为当前用户移除角色
   */
  removeRole(roles: string[]): void {
    for (const val of roles) {
      const idx = this.roles.indexOf(val);
      if (idx !== -1) {
        this.roles.splice(idx, 1);
      }
    }
    this.aclChange.next(this.data);
  }

  /**
   * 当前用户是否有对应角色，其实 `number` 表示Ability
   *
   * - 当 `full: true` 或参数 `null` 时返回 `true`
   * - 若使用 `LcACLType` 参数，可以指定 `mode` 校验模式
   */
  can(roleOrAbility: LcACLCanType | null): boolean {
    // debugger
    const t = this.parseLcACLType(roleOrAbility);
    let result = false;
    if (this.full === true || !roleOrAbility) {
      result = true;
    } else {
      if (t.role && t.role.length > 0) {
        if (typeof t.role === 'string') {
          t.role = [t.role];
        }
        if (t.mode === 'allOf') {
          result = t.role.every(v => {
            if (v.toString().indexOf('-') > -1) {
              let arr = v.split('-');
              return this.roles.includes(arr[0]) && this.types.includes(arr[1]);
            }
            return this.roles.includes(v);
          });
        } else {
          result = t.role.some(v => {
            if (v.toString().indexOf('-') > -1) {
              let arr = v.split('-');
              return this.roles.includes(arr[0]) && this.types.includes(arr[1]);
            }
            return this.roles.includes(v);
          });
        }
      }
      if (t.permissions && t.permissions.length > 0) {
        if (typeof t.permissions === 'string') {
          t.permissions = [t.permissions];
        }
        if (t.mode === 'allOf') {
          result = t.permissions.every(v => this.permissionGroups.includes(v));
        } else {
          result = t.permissions.some(v => this.permissionGroups.includes(v));
        }
      }
      // 用户自定义额外字段
      if (typeof t.extraAll !== 'undefined') {
        result = result && t.extraAll;
      }
      if (typeof t.extraOne !== 'undefined') {
        result = result || t.extraOne;
      }
    }
    return t.except === true ? !result : result;
  }
  /**
   * 路由权限拦截
   * @param url
   * @returns
   */
  canAuthUrl(url: string) {
    return this.authPaths.has(url);
  }
  /**
   * 获取当前url对应是哪一个model_id
   * @param url
   * @returns
   */
  getUrlModeId(url: string) {
    return this.authPaths.get(url);
  }

  getAuthPaths(value: AuthModelList[]): Map<string, string> {
    let reuslt = new Map();

    value.forEach(authModel => {
      let model_id = authModel.model_id;
      reuslt.set(authModel.url, model_id);

      let stack: AuthPathItem[] = [...authModel.menu_list];
      while (stack.length !== 0) {
        const item = stack.pop();

        if (item) {
          reuslt.set(item.url, model_id);

          if (item.children) {
            for (let i = item.children.length - 1; i >= 0; i--) {
              stack.push({ ...item.children[i] });
            }
          }
        }
      }
    });
    return reuslt;
  }
}
